/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataProvider } from 'react-admin';
import { fromDbKeys, toDbKeys, snakeCase, logger } from '@musedrops/shared';
import { createSupabaseClient } from '../lib/database';
import {
  getAllShows,
  getShowWithGenreById,
  createShow,
  updateShow,
} from '../data/show';
import {
  getStoryWithShow,
  listStories,
  createStory,
  updateStory,
} from '../data/story';
import { listGenres } from '../data/genre';
import { orIlikeContains } from '../utils/postgrest';

// Note: Lifecycle processing is now handled automatically by the on-data-change Edge Function
// which receives database triggers on INSERT/UPDATE/DELETE.

/**
 * gen_model_presets pricing columns (#918/#922) don't round-trip through the
 * generic snakeCase()/camelCase() pair: a single-letter segment (m, k)
 * immediately followed by another word collapses two adjacent uppercase
 * letters, and snakeCase() only inserts an underscore before an uppercase
 * letter that follows a lowercase/digit (see shared/src/db-keys.ts). E.g.
 * price_per_k_grounding -> pricePerKGrounding -> price_per_kgrounding.
 * This is the same "known asymmetry" class documented there; per that
 * doc, such columns need a manual map on the write path (see
 * admin/src/data/rec-weight-set.ts for the established pattern).
 */
const GEN_MODEL_PRESET_KEY_FIXES: Record<string, string> = {
  price_per_kgrounding: 'price_per_k_grounding',
  price_per_minput: 'price_per_m_input',
  price_per_moutput: 'price_per_m_output',
  price_per_mcached_input: 'price_per_m_cached_input',
};

function fixGenModelPresetKeys(
  payload: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    result[GEN_MODEL_PRESET_KEY_FIXES[key] ?? key] = value;
  }
  return result;
}

// Browser environment requires explicit Vite env vars
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Check your .env file.'
  );
}

export const supabase = createSupabaseClient({
  url: supabaseUrl,
  anonKey: supabaseAnonKey,
  environment: 'browser',
});

// Issue #699/#700: edge functions validate the caller in-function (admin JWT
// checked against the admins table), so every call must carry the signed-in
// user's session token. The old local-only setAuth(anonKey) override (ES256
// gateway workaround) is gone: functions now have verify_jwt = false, so the
// gateway no longer rejects ES256 session tokens and the real token must
// reach the function for its admin check.

/**
 * Headers for raw fetch calls to Edge Functions. Sends the signed-in user's
 * session token as Bearer (required by in-function admin auth) and the anon
 * key on the apikey header. Prefer supabase.functions.invoke where possible.
 */
export async function getEdgeFunctionAuthHeaders(): Promise<
  Record<string, string>
> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    apikey: supabaseAnonKey,
    Authorization: `Bearer ${session?.access_token ?? supabaseAnonKey}`,
  };
}

const normalizeShowPayload = <T extends Record<string, unknown>>(
  payload: T
): T => {
  if (payload && typeof payload.slug === 'string') {
    return {
      ...payload,
      slug: (payload.slug as string).toLowerCase(),
    } as T;
  }

  return payload;
};

/**
 * Parse the synthetic exposures composite id (`${sessionId}_${storySlug}`).
 * Splits on the FIRST underscore only: session_id is a UUID (no underscores),
 * but a story slug may contain underscores (#748 / I-23).
 */
const parseExposureId = (id: unknown): [string, string] => {
  const idStr = String(id);
  const sepIndex = idStr.indexOf('_');
  return [idStr.slice(0, sepIndex), idStr.slice(sepIndex + 1)];
};

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    try {
      const { pagination, sort, filter: filterParam } = params;
      const { page = 1, perPage = 10 } = pagination || {};
      const rawFilter =
        (filterParam as Record<string, unknown> | undefined) || undefined;
      const filter =
        rawFilter && Object.keys(rawFilter).length > 0 ? rawFilter : undefined;

      if (resource === 'stories') {
        const { data, total } = await listStories({
          page,
          perPage,
          sortField: sort?.field,
          sortOrder: sort?.order,
          filter,
          withTotal: true,
        });

        return {
          data,
          total: total ?? data.length,
        };
      }

      if (resource === 'shows') {
        const { data, total } = await getAllShows({
          page,
          perPage,
          sortField: sort?.field,
          sortOrder: sort?.order,
          filter,
          withTotal: true,
        });

        return {
          data,
          total: total ?? data.length,
        };
      }

      if (resource === 'genres') {
        const { data, total } = await listGenres({
          page,
          perPage,
          sortField: sort?.field,
          sortOrder: sort?.order,
          filter,
          withTotal: true,
        });

        // showCount now comes from the database column, no need to compute manually
        return {
          data,
          total: total ?? data.length,
        };
      }

      // remote_configs: PK is `key` (no `id` column), keep snake_case
      if (resource === 'remote_configs') {
        const rcStart = (page - 1) * perPage;
        const rcEnd = rcStart + perPage - 1;
        const sortField = sort?.field || 'key';
        let query = supabase
          .from(resource)
          .select('*', { count: 'exact' })
          .range(rcStart, rcEnd)
          .order(sortField, { ascending: (sort?.order ?? 'ASC') === 'ASC' });

        if (filter) {
          Object.entries(filter).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') {
              query = query.eq(k, v as string);
            }
          });
        }

        const { data, error, count } = await query;
        if (error) throw error;

        // Add synthetic `id` field (React-Admin requires it)
        const rows = (data ?? []).map((row: Record<string, unknown>) => ({
          ...row,
          id: row.key,
        }));

        return { data: rows as any, total: count || 0 };
      }

      const { field, order } = sort || { field: 'id', order: 'ASC' };
      const sortField = field ? snakeCase(field) : undefined;

      // Extract search query for process_logs before normalization
      let searchQuery: string | undefined;
      let filterWithoutSearch = filter;

      if (
        (resource === 'process_logs' ||
          resource === 'process_logs_with_names') &&
        filter?.q &&
        typeof filter.q === 'string'
      ) {
        searchQuery = filter.q;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { q, ...restFilter } = filter as {
          q: string;
          [key: string]: unknown;
        };
        filterWithoutSearch = restFilter;
      }

      const normalizedFilter = filterWithoutSearch
        ? toDbKeys(filterWithoutSearch)
        : undefined;
      const start = (page - 1) * perPage;
      const end = start + perPage - 1;

      let query = supabase
        .from(resource)
        .select('*', { count: 'exact' })
        .range(start, end);

      // Handle text search for process_logs
      if (
        (resource === 'process_logs' ||
          resource === 'process_logs_with_names') &&
        searchQuery
      ) {
        // Search across entity_id, entity_name, process, and message
        query = query.or(
          orIlikeContains(
            ['entity_id', 'entity_name', 'process', 'message'],
            searchQuery
          )
        );
      }

      if (normalizedFilter) {
        Object.entries(normalizedFilter).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            return;
          }
          if (Array.isArray(value)) {
            query = query.in(key, value as unknown[]);
          } else {
            query = query.eq(key, value as string | number | boolean);
          }
        });
      }

      if (sortField) {
        query = query.order(sortField, { ascending: order === 'ASC' });
      }

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      let processedData = fromDbKeys<Record<string, unknown>[]>(
        (data ?? []) as Record<string, unknown>[]
      );

      // Add synthetic id field for exposures table (composite primary key)
      if (resource === 'exposures') {
        processedData = processedData.map(record => ({
          ...record,
          id: `${record.sessionId}_${record.storySlug}`,
        }));
      }

      return {
        data: processedData,
        total: count || 0,
      };
    } catch (error) {
      logger.error('getList failed', { error, resource, params });
      throw error;
    }
  },

  getOne: async (resource, params) => {
    if (resource === 'stories') {
      const data = await getStoryWithShow(String(params.id));
      return { data };
    }

    if (resource === 'shows') {
      const data = await getShowWithGenreById(String(params.id));
      return { data };
    }

    const { data, error } = await supabase
      .from(resource)
      .select('*')
      .eq('id', params.id)
      .single();

    if (error) throw error;

    return {
      data: fromDbKeys<Record<string, unknown>>(
        data as Record<string, unknown>
      ) as any,
    };
  },

  getMany: async (resource, params) => {
    const { data, error } = await supabase
      .from(resource)
      .select('*')
      .in('id', params.ids);

    if (error) throw error;

    return {
      data: fromDbKeys<Record<string, unknown>[]>(
        (data ?? []) as Record<string, unknown>[]
      ) as any,
    };
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const start = (page - 1) * perPage;
    const end = start + perPage - 1;

    let query = supabase
      .from(resource)
      .select('*', { count: 'exact' })
      .eq(params.target, params.id)
      .range(start, end);

    const normalizedField = field ? snakeCase(field) : undefined;
    if (normalizedField) {
      query = query.order(normalizedField, { ascending: order === 'ASC' });
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      data: fromDbKeys<Record<string, unknown>[]>(
        (data ?? []) as Record<string, unknown>[]
      ) as any,
      total: count || 0,
    };
  },

  create: async (resource, params) => {
    // Type-safe wrappers for core entities
    if (resource === 'stories') {
      const storyData = params.data as any;
      const createdStory = await createStory(storyData);
      // Note: Lifecycle is now handled by on-data-change Edge Function
      return { data: createdStory };
    }

    if (resource === 'shows') {
      const showData = normalizeShowPayload(
        params.data as Record<string, unknown>
      );
      const createdShow = await createShow(showData);
      // Note: Lifecycle is now handled by on-data-change Edge Function
      return { data: createdShow };
    }

    // remote_configs: PK is `key`, data is already snake_case
    if (resource === 'remote_configs') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...insertData } = params.data as Record<string, unknown>;
      const { data, error } = await supabase
        .from(resource)
        .insert(insertData)
        .select()
        .single();
      if (error) throw error;
      return { data: { ...data, id: data.key } as any };
    }

    // Generic fallback for other resources
    const dataToInsert = { ...(params.data as Record<string, unknown>) };
    delete dataToInsert.id;
    delete dataToInsert.createdAt;
    delete dataToInsert.updatedAt;

    let insertPayload = toDbKeys(dataToInsert);
    if (resource === 'gen_model_presets') {
      insertPayload = fixGenModelPresetKeys(insertPayload);
    }

    const { data, error } = await supabase
      .from(resource)
      .insert(insertPayload)
      .select()
      .single();

    if (error) {
      logger.error('Supabase create error', { error, resource });
      throw error;
    }

    const result = fromDbKeys<Record<string, unknown>>(
      data as Record<string, unknown>
    );

    // Note: Lifecycle is now handled by on-data-change Edge Function

    return { data: result as any };
  },

  update: async (resource, params) => {
    // Type-safe wrappers for core entities
    if (resource === 'stories') {
      const storyData = { ...(params.data as any), id: params.id };
      await updateStory(storyData);
      // Note: updateStory() calls onStoryUpdated() which handles lifecycle + publishedAt
      // Fetch updated story to return
      const updatedStory = await getStoryWithShow(String(params.id));
      return { data: updatedStory };
    }

    if (resource === 'shows') {
      const showData = {
        ...normalizeShowPayload(params.data as Record<string, unknown>),
        id: params.id,
      };
      await updateShow(showData as any);
      // Note: Lifecycle is now handled by on-data-change Edge Function
      // Fetch updated show to return
      const updatedShow = await getShowWithGenreById(String(params.id));
      return { data: updatedShow };
    }

    // remote_configs: PK is `key`, data is already snake_case
    if (resource === 'remote_configs') {
      const raw = params.data as Record<string, unknown>;
      // Only pass mutable fields — exclude PK and auto-managed columns
      const updateFields: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (!['id', 'key', 'updated_at', 'updated_by'].includes(k)) {
          updateFields[k] = v;
        }
      }
      const { data, error } = await supabase
        .from(resource)
        .update(updateFields)
        .eq('key', params.id)
        .select()
        .single();
      if (error) throw error;
      return { data: { ...data, id: data.key } as any };
    }

    // Generic fallback for other resources
    const updatedData = { ...(params.data as Record<string, unknown>) };
    delete updatedData.createdAt;
    delete updatedData.updatedAt;

    let updatePayload = toDbKeys(updatedData);
    if (resource === 'gen_model_presets') {
      updatePayload = fixGenModelPresetKeys(updatePayload);
    }
    delete updatePayload.updated_at;

    const { data, error } = await supabase
      .from(resource)
      .update(updatePayload)
      .eq('id', params.id)
      .select()
      .single();

    if (error) throw error;

    const result = fromDbKeys<Record<string, unknown>>(
      data as Record<string, unknown>
    );

    // Note: Lifecycle is now handled by on-data-change Edge Function

    return { data: result as any };
  },

  updateMany: async (resource, params) => {
    const { data, error } = await supabase
      .from(resource)
      .update(toDbKeys(params.data as Record<string, unknown>))
      .in('id', params.ids)
      .select();

    if (error) {
      logger.error('updateMany error', { error, resource });
      throw error;
    }

    const updatedRecords = fromDbKeys<Record<string, unknown>[]>(
      (data ?? []) as Record<string, unknown>[]
    );

    // Note: Lifecycle is now handled by on-data-change Edge Function
    const entityIds = updatedRecords
      .map(record => record.id)
      .filter((id): id is string => typeof id === 'string');

    return { data: entityIds };
  },

  delete: async (resource, params) => {
    // remote_configs: PK is `key`
    if (resource === 'remote_configs') {
      const { data, error } = await supabase
        .from(resource)
        .delete()
        .eq('key', params.id)
        .select()
        .single();
      if (error) throw error;
      return { data: { ...data, id: data.key } as any };
    }

    // Handle composite primary key for exposures table
    if (resource === 'exposures') {
      const [sessionId, storySlug] = parseExposureId(params.id);
      const { data, error } = await supabase
        .from(resource)
        .delete()
        .eq('session_id', sessionId)
        .eq('story_slug', storySlug)
        .select()
        .single();

      if (error) throw error;

      return {
        data: fromDbKeys<Record<string, unknown>>(
          data as Record<string, unknown>
        ) as any,
      };
    }

    const { data, error } = await supabase
      .from(resource)
      .delete()
      .eq('id', params.id)
      .select()
      .single();

    if (error) throw error;

    return {
      data: fromDbKeys<Record<string, unknown>>(
        data as Record<string, unknown>
      ) as any,
    };
  },

  deleteMany: async (resource, params) => {
    // Handle composite primary key for exposures table
    if (resource === 'exposures') {
      // Delete each exposure individually since we have composite keys
      const deletePromises = params.ids.map(async id => {
        const [sessionId, storySlug] = parseExposureId(id);
        return supabase
          .from(resource)
          .delete()
          .eq('session_id', sessionId)
          .eq('story_slug', storySlug);
      });

      const results = await Promise.all(deletePromises);
      const error = results.find(r => r.error)?.error;

      if (error) throw error;

      return { data: params.ids };
    }

    const { error } = await supabase
      .from(resource)
      .delete()
      .in('id', params.ids);

    if (error) throw error;

    return { data: params.ids };
  },
};
